import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, Form } from 'formik';
import styled from 'styled-components';
import { Box, Button, Text, InputGroup, Input } from '@components/common';
import { InputField } from '@components/form';
import DefaultLoader from '@components/loading/DefaultLoader';

import API from '@utils/api';
import validationSchema from './schema';
import SvgIcon from '@components/SvgIcon';
import theme from '@styles/theme';
import { getErrors } from '@utils/error';
import { trackKeyRelease } from '@services/analytics';

const FlexButton = styled(Button)`
  display: flex;
`;

const LoadContainer = styled(Box)`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  right: 0;
  botoom: 0;
`;

class SignKeyForm extends React.Component {
  static propTypes = {
    user: PropTypes.object,
    onSubmit: PropTypes.func,
    onViewKey: PropTypes.func,
    isKeyReleaseSigned: PropTypes.bool,
    hideButtons: PropTypes.bool,
    propertyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  };

  handleSubmit = async (values, actions) => {
    const { onSubmit, propertyID } = this.props;
    const { setSubmitting, setErrors } = actions;
    setSubmitting(true);
    try {
      const { signature } = values;
      let isSigned = false;
      if (signature != '') {
        const [firstName, lastName] = signature.split(' ');
        await API.setKeyReleaseInfo({
          propertyID,
          firstName,
          lastName
        });
        isSigned = true;
      }
      setSubmitting(false);
      onSubmit(signature, isSigned);
      trackKeyRelease();
    } catch (err) {
      const error = getErrors(err);
      if (error.message) {
        setErrors({
          lastName: error.message
        });
      } else {
        setErrors({
          lastName:
            Object.values(error).pop() ||
            'Sign your full name as it appears on your account'
        });
        setErrors(error);
      }
    }
    setSubmitting(false);
  };

  renderView = () => {
    const { onSubmit, onViewKey, user, hideButtons } = this.props;
    const { firstName, lastName } = user;
    return (
      <Box display="flex" flexDirection="column">
        <Text fontSize={1}>
          To service your apartment, we will need to make a copy of your key,
          and keep it in a locked box at our office. Type your name below to
          grant permission.
        </Text>
        <FlexButton
          variant="secondary"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          width={1}
          my={4}
          onClick={onViewKey}
        >
          <Text>Read the full key release</Text>
          <SvgIcon variant={theme.colors.grey[2]} name="forward" height={16} />
        </FlexButton>

        <InputGroup>
          <Input
            readOnly
            mb={4}
            type="text"
            value={`${firstName} ${lastName}`}
          />
        </InputGroup>

        {!hideButtons && (
          <Button flex={1} mb={4} type="button" onClick={onSubmit}>
            Continue
          </Button>
        )}
      </Box>
    );
  };

  render() {
    const { isKeyReleaseSigned } = this.props;
    if (isKeyReleaseSigned) {
      return this.renderView();
    }

    return (
      <Formik
        initialValues={{ signature: '' }}
        onSubmit={this.handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isValid, isSubmitting }) => {
          const { onViewKey, hideButtons } = this.props;
          return (
            <Form>
              <Box display="flex" flexDirection="column" position="relative">
                {isSubmitting && (
                  <LoadContainer>
                    <DefaultLoader size={64} />
                  </LoadContainer>
                )}
                <Text fontSize={1}>
                  Your housekeeper will pick up a key from your concierge, and
                  return it once they have finished. Please enter your full name
                  to sign your consent.
                </Text>
                <FlexButton
                  variant="secondary"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width={1}
                  my={4}
                  type="button"
                  onClick={onViewKey}
                >
                  <Text>Read full terms</Text>
                  <SvgIcon
                    variant={theme.colors.grey[2]}
                    name="forward"
                    height={16}
                  />
                </FlexButton>

                <Field
                  component={InputField}
                  mb={4}
                  name="signature"
                  type="text"
                  label="Signature"
                />

                {!hideButtons && (
                  <Button
                    flex={1}
                    mb={4}
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </Form>
          );
        }}
      </Formik>
    );
  }
}

export default SignKeyForm;
